import ViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import EmojiCategory from 'enums/EmojiCategory'
import Category, { CategoryStrings } from 'enums/Category'
import AnalysisType, { AnalysisTypeStrings } from 'enums/AnalysisType'
import MeasureType, { MeasureTypeStrings } from 'enums/MeasureType'
import SortByOptions, { SortByOptionsStrings } from 'enums/SortByOptions'

export const getEmojiCategory = (viewOption?: InsightViewOptionStrings) => {
  switch (viewOption) {
    case ViewOption.EMOJIS_GROUP:
      return EmojiCategory.TYPE
    case ViewOption.EMOJIS_EMOTION:
      return EmojiCategory.EMOTION
    case ViewOption.EMOJIS_EMOJI:
      return EmojiCategory.EMOJI
    default:
      return undefined
  }
}

export const isMeasurementType = (view: InsightViewOptionStrings) =>
  view === ViewOption.STAR_RATINGS || view === ViewOption.SURVEY_SCORES

export const getInsightCategory = (
  viewOption: InsightViewOptionStrings,
  analysisType?: AnalysisTypeStrings
): CategoryStrings[] => {
  if (
    analysisType &&
    (analysisType === AnalysisType.SW_BENCHMARK || analysisType === AnalysisType.AM_BENCHMARK)
  ) {
    switch (viewOption) {
      case ViewOption.BRANDS:
        return [Category.BENCHMARK_BRANDS]
      case ViewOption.HASHTAGS:
        return [Category.BENCHMARK_HASHTAGS]
      case ViewOption.PEOPLE:
        return [Category.BENCHMARK_PEOPLE]
      case ViewOption.THINGS:
        return [Category.BENCHMARK_THINGS]
      case ViewOption.TERMS:
        return [Category.BENCHMARK_PHRASES]
      case ViewOption.BIOTEXT:
        return [Category.BENCHMARK_BIO_TEXT]
      case ViewOption.EMOJIS_GROUP:
      case ViewOption.EMOJIS_EMOTION:
      case ViewOption.EMOJIS_EMOJI:
      default:
        return [Category.BENCHMARK_EMOJI]
    }
  }

  if (
    analysisType &&
    (analysisType === AnalysisType.AM_TRENDING || analysisType === AnalysisType.SW_TRENDING)
  ) {
    switch (viewOption) {
      case ViewOption.THINGS:
        return [Category.TRENDING_THINGS]
      case ViewOption.TERMS:
        return [Category.TRENDING_TERMS]
      case ViewOption.HASHTAGS:
        return [Category.TRENDING_HASHTAGS]
      case ViewOption.BRANDS:
        return [Category.TRENDING_BRANDS]
      case ViewOption.PEOPLE:
        return [Category.TRENDING_PEOPLE]
      case ViewOption.EMOJIS_GROUP:
      case ViewOption.EMOJIS_EMOTION:
      case ViewOption.EMOJIS_EMOJI:
      default:
        return [Category.TRENDING_EMOJI]
    }
  }

  switch (viewOption) {
    case ViewOption.AUTHORS:
      return [Category.AUTHORS]

    case ViewOption.BRANDS:
      return [Category.ORG_PRODUCTS]

    case ViewOption.PRODUCT_NAME:
      return [Category.PRODUCT_NAME]

    case ViewOption.HASHTAGS:
      return [Category.HASHTAGS]

    case ViewOption.TERMS:
      return [Category.PHRASES]

    case ViewOption.POSITIVE_NEGATIVE:
    case ViewOption.POSITIVE_NEGATIVE_NEUTRAL:
      return [Category.SENTIMENT]

    case ViewOption.PEOPLE:
      return [Category.PEOPLE]

    case ViewOption.THINGS:
      return [Category.THINGS]

    case ViewOption.STAR_RATINGS:
      return [Category.STAR_RATING]

    case ViewOption.SURVEY_SCORES:
      return [Category.SURVEY_SCORE]

    case ViewOption.ATTRIBUTES:
      return [Category.LIKES, Category.DISLIKES]

    case ViewOption.EMOTIONS:
      return [Category.POSITIVE_EMOTIONS, Category.NEGATIVE_EMOTIONS]

    case ViewOption.BEHAVIORS:
      return [Category.POSITIVE_BEHAVIORS, Category.NEGATIVE_BEHAVIORS]

    case ViewOption.EMOTIONS_BEHAVIORS_TO_THINGS:
      return [
        Category.POSITIVE_EMOTIONS_BEHAVIORS_TO_THINGS,
        Category.NEGATIVE_EMOTIONS_BEHAVIORS_TO_THINGS
      ]

    case ViewOption.THINGS_TO_EMOTIONS_BEHAVIORS:
      return [
        Category.THINGS_TO_POSITIVE_EMOTIONS_BEHAVIORS,
        Category.THINGS_TO_NEGATIVE_EMOTIONS_BEHAVIORS
      ]

    case ViewOption.SOURCES:
      return [Category.SOURCES]

    case ViewOption.MALE_FEMALE:
      return [Category.BENCHMARK_GENDER]

    case ViewOption.AGE:
      return [Category.BENCHMARK_AGE]

    case ViewOption.INTERESTS:
      return [Category.BENCHMARK_INTERESTS]

    case ViewOption.PROFESSIONS:
      return [Category.BENCHMARK_PROFESSIONS]

    case ViewOption.ETHNICITY:
      return [Category.BENCHMARK_ETHNICITY]

    case ViewOption.BIOTEXT:
      return [Category.BENCHMARK_BIO_TEXT]

    case ViewOption.EMOJIS_GROUP:
    case ViewOption.EMOJIS_EMOTION:
    case ViewOption.EMOJIS_EMOJI:
      return [Category.EMOJI]

    case ViewOption.HOUR_OF_DAY:
      return [Category.POST_ACTIVITY_HOUR_OF_DAY]

    case ViewOption.DAY_OF_WEEK:
      return [Category.POST_ACTIVITY_DAY_OF_WEEK]

    case ViewOption.HOUR_OF_WEEK:
      return [Category.POST_ACTIVITY_HOUR_OF_WEEK]

    case ViewOption.LANGUAGE:
      return [Category.LANGUAGES_ANALYSIS]

    case ViewOption.ALL_SOURCES:
    case ViewOption.BLOGS:
    case ViewOption.FORUMS:
    case ViewOption.NEWS:
      return [Category.DOMAINS]
    case ViewOption.TAGS:
      return [Category.TAGS]
    case ViewOption.DOC_HIERARCHY:
      return [Category.DOC_HIERARCHY]
    case ViewOption.LOGOS:
      return [Category.LOGOS]
    case ViewOption.DETECT_OBJECTS:
      return [Category.DETECT_OBJECTS]
    case ViewOption.DETECT_SCENES:
      return [Category.DETECT_SCENES]
    case ViewOption.DETECT_EMOTIONS:
      return [Category.DETECT_EMOTIONS]
    default:
      throw new Error(`No mapping specified with viewOption: ${viewOption}`)
  }
}

export const getMeasureType = (viewOption: InsightViewOptionStrings): MeasureTypeStrings[] => {
  switch (viewOption) {
    case ViewOption.MENTIONS:
      return [MeasureType.MENTIONS]
    case ViewOption.POSTS:
      return [MeasureType.POSTS]
    case ViewOption.IMPRESSIONS:
      return [MeasureType.IMPRESSIONS]
    case ViewOption.NET_SENTIMENT:
      return [MeasureType.NET_SENTIMENT]
    case ViewOption.PASSION_INTENSITY:
      return [MeasureType.PASSION_INTENSITY]
    case ViewOption.POSITIVE:
      return [MeasureType.POSITIVES]
    case ViewOption.NEGATIVE:
      return [MeasureType.NEGATIVES]
    case ViewOption.UNIQUE_AUTHORS:
      return [MeasureType.UNIQUE_AUTHORS]
    case ViewOption.ENGAGEMENTS:
      return [MeasureType.ENGAGEMENTS]
    case ViewOption.ENGAGEMENT_RATIO_ONLY:
      return [MeasureType.ENGAGEMENT_RATIO_ONLY]
    case ViewOption.COMMENTS:
      return [MeasureType.COMMENTS]
    case ViewOption.LIKES:
      return [MeasureType.LIKES]
    case ViewOption.SHARES:
      return [MeasureType.SHARES]
    case ViewOption.VIEWS:
      return [MeasureType.VIEWS]
    case ViewOption.DISLIKES:
      return [MeasureType.DISLIKES]
    case ViewOption.SCORE:
      return [MeasureType.SCORE]
    case ViewOption.STAR_RATINGS:
      return [MeasureType.OVERALL_STAR_RATINGS_AVERAGE]
    case ViewOption.SURVEY_SCORES:
      return [MeasureType.OVERALL_SURVEY_SCORES_AVERAGE]
    default:
      throw new Error(`No MeasureType specified with viewOption: ${viewOption}`)
  }
}

export const getSortByOption = (viewOption: InsightViewOptionStrings): SortByOptionsStrings => {
  switch (viewOption) {
    case ViewOption.BY_ENGAGEMENTS:
      return SortByOptions.MOST_ENGAGEMENTS
    case ViewOption.BY_REPOSTS:
      return SortByOptions.MOST_RETWEETS
    case ViewOption.BY_REPLIES:
      return SortByOptions.MOST_REPLIES
    case ViewOption.BY_DATE:
      return SortByOptions.MOST_RECENT
    case ViewOption.BY_LINK_MENTIONS:
      return SortByOptions.MOST_SHARED
    case ViewOption.BY_VIEWS:
      return SortByOptions.MOST_VIEWS
    default:
      throw new Error(`No SortBy specified with viewOption: ${viewOption}`)
  }
}
